import { TargetProvider } from "@modernatx-internal/ui-kit-react";
import { useRouter } from "next/router";
import React from "react";

const getMouseEventOpensInNewWindow = (event: React.MouseEvent) => {
  const target = event.currentTarget.getAttribute("target");

  return (
    (target && target !== "_self") ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    (event.nativeEvent && event.nativeEvent.which === 2)
  );
};

export const PageTargetProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { push } = useRouter();

  const handleTargetClick = React.useCallback(
    (e: React.MouseEvent) => {
      const href = e.currentTarget.getAttribute("href");

      if (e.currentTarget instanceof HTMLAnchorElement) {
        if (href?.startsWith("/") && !getMouseEventOpensInNewWindow(e)) {
          e.preventDefault();
          const scroll = e.currentTarget.getAttribute("data-scroll");

          if (!scroll) {
            push(href);
          } else {
            push(href, undefined, { scroll: false });

            const element = document.getElementById(scroll);

            if (scroll !== "false" && element && element.getBoundingClientRect().top <= 0) {
              document.getElementById(scroll)?.scrollIntoView();
            }
          }
        }
      }
    },
    [push]
  );

  type TargetResolver = NonNullable<React.ComponentProps<typeof TargetProvider>["targetResolver"]>;

  const handleResolveTarget = React.useCallback<TargetResolver>((href) => {
    if (!href) {
      return undefined;
    }

    if (href?.startsWith("/")) {
      return {
        href,
        target: "_self"
      };
    }

    return {
      href,
      target: "_blank"
    };
  }, []);

  return (
    <TargetProvider clickHandler={handleTargetClick} targetResolver={handleResolveTarget}>
      {children}
    </TargetProvider>
  );
};
